import React, { useEffect, useState } from "react";
import "./styles.scss";
import { Col, Container, FormGroup, Row } from "react-bootstrap";
import MediaButton from "../media-button/MediaButton";
import { BiText } from "react-icons/bi";
import { FaCameraRetro, FaPencilAlt } from "react-icons/fa";
import { HiLink } from "react-icons/hi";
import MediaFile from "../media-card/file/MediaFile";
import MediaText from "../media-card/text/MediaText";
import MediaVideo from "../media-card/video/MediaVideo";
import MediaURL, { MediaURLContent } from "../media-card/url/MediaURL";
import { RiFileLine } from "react-icons/ri";
import {
  DragDropContext,
  DropResult,
  Droppable,
  Draggable,
  DraggableProvided,
} from "react-beautiful-dnd";
import ConfirmationModal from "../../../../../../shared/components/confirmationModal/ConfirmationModal";
import UserNotification from "../../../../../../shared/components/Notification/UserNotification";
import { useAppUser } from "../../../../../../contexts/user-context/UserContext";
import { useLocation } from "react-router-dom";
import {
  useWorkShop,
  WorkshopSectionContent,
  WorkshopSectionContentType,
} from "../../../../../../contexts/workshop-context/WorkshopContext";
import { MediaFileContent } from "../media-card/MediaCard";
import getIconByType from "../../../../../../shared/resources/file-types/file-types";

interface State {
  showAcceptConfirmation: boolean;
  showDiscardConfirmation: boolean;
  showRemoveConfirmation: boolean;
}

const initState: State = {
  showAcceptConfirmation: false,
  showDiscardConfirmation: false,
  showRemoveConfirmation: false,
};

const NewSubSection = () => {
  const workShop = useWorkShop();
  const appUser = useAppUser();
  const { updateResource, selectedSubSection } = workShop;
  const [loading, setLoading] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [state, setState] = useState(initState);
  const [isSave, setIsSave] = useState(true);
  const [uploading, setUploading] = useState(false);
  const editPath: boolean = useLocation().pathname.includes("/edit");

  useEffect(() => setIsSave(true), [workShop.selectedSubSection.orderNo]);

  const getResourceCard = (
    resource: WorkshopSectionContent,
    provided: DraggableProvided
  ) => {
    switch (resource.type) {
      case WorkshopSectionContentType.URL:
        return (
          <MediaURL
            resource={resource}
            uploading={uploading}
            provided={provided}
          />
        );
      case WorkshopSectionContentType.TEXT:
        return (
          <MediaText
            resource={resource}
            uploading={uploading}
            provided={provided}
          />
        );
      case WorkshopSectionContentType.FILE_URL:
        return (
          <MediaFile
            resource={resource}
            uploading={uploading}
            onUploading={setUploading}
            provided={provided}
            onFileSelected={(file) => {
              const url = URL.createObjectURL(file);
              resource.content = JSON.stringify({
                url,
                fileName: file.name,
                type: file.name.split(".").pop(),
              });
              if (file.type.split("/")[0] === "image")
                resource.type = WorkshopSectionContentType.IMAGE_URL;
              updateResource(selectedSubSection, resource);
            }}
          />
        );
      case WorkshopSectionContentType.IMAGE_URL:
        return (
          <MediaFile
            resource={resource}
            uploading={uploading}
            provided={provided}
            onUploading={setUploading}
            onFileSelected={(file) => {
              const url = URL.createObjectURL(file);
              resource.content = JSON.stringify({
                url,
                filaName: file.name,
                type: file.name.split(".").pop(),
              });
              if (file.type.split("/")[0] !== "image")
                resource.type = WorkshopSectionContentType.FILE_URL;
              updateResource(selectedSubSection, resource);
            }}
          />
        );
      default:
        return (
          <MediaVideo
            resource={resource}
            provided={provided}
            uploading={uploading}
            onUploading={setUploading}
            onFileSelected={(file) => {
              const url = URL.createObjectURL(file);
              resource.content = JSON.stringify({
                url,
                type: file.name.split(".").pop(),
              });
              updateResource(selectedSubSection, resource);
            }}
          />
        );
    }
  };

  const getURLContent = (content: string): MediaURLContent => {
    try {
      const data = JSON.parse(content);
      if (data) {
        return data;
      } else {
        return { text: content, url: content };
      }
    } catch (e) {
      return { text: content, url: content };
    }
  };

  const getFileContent = (content: string): MediaFileContent => {
    try {
      const data: MediaFileContent = JSON.parse(content);
      if (data) {
        return data;
      } else {
        return { fileName: data, url: data, type: "all" };
      }
    } catch (e) {
      return { fileName: content, url: content, type: "all" };
    }
  };

  const getResourcePreview = (resource: WorkshopSectionContent) => {
    switch (resource.type) {
      case WorkshopSectionContentType.URL:
        return (
          <div
            className="d-flex"
            style={{ fontSize: "24px", marginBottom: "16px" }}
          >
            <div style={{ marginRight: "15px" }}>
              <HiLink />
            </div>
            <a
              href={getURLContent(resource.content).url}
              rel="noopener noreferrer"
              target="_blank"
            >
              {getURLContent(resource.content).text}
            </a>
          </div>
        );
      case WorkshopSectionContentType.TEXT:
        return (
          <p
            className="text-justify"
            style={{ color: "#646464", fontSize: "24px", marginBottom: "16px" }}
          >
            {resource.content}
          </p>
        );
      case WorkshopSectionContentType.IMAGE_URL:
        return (
          <div className="text-center" style={{ marginBottom: "16px" }}>
            <img
              src={getFileContent(resource.content).url}
              style={{ width: "40%" }}
              alt=""
            />
          </div>
        );
      case WorkshopSectionContentType.FILE_URL:
        return (
          <div
            className="text-left"
            style={{
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={getIconByType(getFileContent(resource.content).type)}
              alt=""
              style={{ width: "30px" }}
            />
            <a
              style={{ fontSize: "24px", marginLeft: "10px" }}
              href={getFileContent(resource.content).url}
              rel="noopener noreferrer"
              target="_blank"
            >
              {getFileContent(resource.content).fileName}
            </a>
          </div>
        );
      default:
        return (
          <video
            style={{ width: "100%", cursor: "default", marginBottom: "16px" }}
            key={getFileContent(resource.content).url}
            controls
          >
            <source src={getFileContent(resource.content).url} />
          </video>
        );
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (result.destination)
      workShop.moveResource(result.source.index, result.destination?.index);
    setShowButtons(true);
  };

  const [showButtons, setShowButtons] = useState(true);

  return (
    <Container fluid className="pb-4 px-5 new-sub-section-title">
      {(appUser.userRole.isAdmin || appUser.userRole.isModerator) &&
        showButtons &&
        workShop.selectedSubSection.mode !== "PREVIEW" &&
        selectedSubSection.edited && (
          <Row className="text-right">
            <Col xs={12}>
              <button
                className={uploading ? "disable-button" : "clear-button "}
                style={{ marginRight: "15px" }}
                onClick={() => {
                  if (!uploading)
                    setState({ ...state, showDiscardConfirmation: true });
                }}
              >
                Discard
              </button>
              <button
                className={uploading ? "disable-button" : "save-button "}
                style={{ marginLeft: "15px" }}
                onClick={() => {
                  if (!uploading)
                    if (
                      workShop.selectedSubSection.subSectionTitle.length < 1
                    ) {
                      setTitleError(true);
                    } else {
                      setState({ ...state, showAcceptConfirmation: true });
                    }
                }}
              >
                {isSave ? "Save" : "Update"}
              </button>
            </Col>
          </Row>
        )}
      {(appUser.userRole.isAdmin || appUser.userRole.isModerator) && editPath && (
        <Row>
          <Col xs={12} className="d-flex p-0 justify-content-end">
            {workShop.selectedSubSection.mode === "PREVIEW" && (
              <div
                className="edit"
                onClick={(e) => {
                  workShop.changeSubsectionMode({
                    ...workShop.selectedSubSection,
                    mode: "EDIT",
                  });
                  setIsSave(false);
                }}
              >
                <FaPencilAlt /> <span>Edit</span>
              </div>
            )}
          </Col>
        </Row>
      )}
      {(appUser.userRole.isAdmin || appUser.userRole.isModerator) &&
      workShop.selectedSubSection.mode === "EDIT" &&
      editPath ? (
        <>
          <Row>
            <Col xs={12}>
              <FormGroup>
                <label
                  className="font-weight-bold form-label"
                  style={{ fontSize: "20px" }}
                >
                  Sub Section Title
                  <span className="ml-1" style={{ color: "red" }}>
                    *
                  </span>
                </label>
                <input
                  className={titleError ? "app-input invalid" : "app-input"}
                  value={workShop.selectedSubSection.subSectionTitle}
                  onChange={(e) => {
                    console.log(e.target.value);
                    workShop.updateSubSection({
                      ...workShop.selectedSubSection,
                      subSectionTitle: e.target.value,
                    });
                  }}
                  onFocus={() => setTitleError(false)}
                />
                {titleError && (
                  <label className="error-message">
                    Sub Section Title can not be empty.
                  </label>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row className="justify-content-between px-3 pt-3">
            <MediaButton
              icon={<BiText />}
              text="Add Text"
              disabled={uploading}
              onClick={() => {
                workShop.addResource(
                  workShop.selectedSubSection,
                  WorkshopSectionContentType.TEXT
                );
              }}
            />
            <MediaButton
              icon={<RiFileLine />}
              text="Add File"
              disabled={uploading}
              onClick={() => {
                workShop.addResource(
                  workShop.selectedSubSection,
                  WorkshopSectionContentType.FILE_URL
                );
              }}
            />
            <MediaButton
              icon={<FaCameraRetro />}
              text="Add Video"
              disabled={uploading}
              onClick={() => {
                workShop.addResource(
                  workShop.selectedSubSection,
                  WorkshopSectionContentType.VIDEO_URL
                );
              }}
            />
            <MediaButton
              icon={<HiLink />}
              text="Add URL"
              disabled={uploading}
              onClick={() => {
                workShop.addResource(
                  workShop.selectedSubSection,
                  WorkshopSectionContentType.URL
                );
              }}
            />
          </Row>
          <Row>
            <Col xs={12}>
              <DragDropContext
                onDragEnd={onDragEnd}
                onDragStart={() => {
                  setShowButtons(false);
                }}
              >
                <Container fluid>
                  <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                      <Row {...provided.droppableProps} ref={provided.innerRef}>
                        {workShop.selectedSubSection.workshopSectionContent.map(
                          (resource) => (
                            <Col xs={12} className="p-0" key={resource.orderNo}>
                              <Draggable
                                draggableId={resource.orderNo + ""}
                                index={resource.orderNo}
                                isDragDisabled={uploading}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    className="pt-5"
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    {getResourceCard(resource, provided)}
                                  </div>
                                )}
                              </Draggable>
                            </Col>
                          )
                        )}
                        {provided.placeholder}
                      </Row>
                    )}
                  </Droppable>
                </Container>
              </DragDropContext>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row>
            <Col xs={12} className="mb-4">
              <label className="font-weight-bold subsection-summery-title">
                {workShop.selectedSubSection.subSectionTitle}
              </label>
            </Col>
          </Row>
          {workShop.selectedSubSection.workshopSectionContent.map((res) => (
            <Row key={res.orderNo} className="mb-0 p-0">
              <Col xs={12}>{getResourcePreview(res)}</Col>
            </Row>
          ))}
        </>
      )}
      {state.showAcceptConfirmation && (
        <ConfirmationModal
          loading={loading}
          children={{
            headerText: "Confirm ".concat(isSave ? "Save" : "Updates"),
            bodyText: (
              <>
                Are you sure you want to {isSave ? "Save" : "Update"} the
                content?
              </>
            ),
            cancelButtonText: "Cancel",
            confirmButtonText: isSave ? "Save" : "Update",
            confirmButtonColor: loading ? "grayStatic" : "blue",
          }}
          show={state.showAcceptConfirmation}
          onCancel={() => setState({ ...state, showAcceptConfirmation: false })}
          onConfirm={() => {
            setLoading(true);
            workShop.saveData(
              () => {
                setLoading(false);
                setState({ ...state, showAcceptConfirmation: false });
                UserNotification({
                  isSuccess: true,
                  message: `Workshop subsection ${
                    isSave ? "Saved" : "Updated"
                  } successfully!`,
                });
              },
              () => {
                setState({ ...state, showAcceptConfirmation: false });
                setLoading(false);
                UserNotification({
                  isSuccess: false,
                  message: `Workshop subsection ${
                    isSave ? "Saved" : "Updated"
                  } failed!\nPlease check your connection.`,
                });
              }
            );
          }}
        />
      )}
      {state.showDiscardConfirmation && (
        <ConfirmationModal
          loading={false}
          children={{
            headerText: "confirmation",
            bodyText: (
              <>
                Are you sure, you want to <b>DISCARD</b> changes?
              </>
            ),
            cancelButtonText: "Cancel",
            confirmButtonText: "Discard",
            confirmButtonColor: "red",
          }}
          show={state.showDiscardConfirmation}
          onCancel={() =>
            setState({ ...state, showDiscardConfirmation: false })
          }
          onConfirm={() => {
            workShop.discardChanges();
            setState({ ...state, showDiscardConfirmation: false });
          }}
        />
      )}
    </Container>
  );
};

export default NewSubSection;
